import { useTranslation } from 'react-i18next';

import { formatAuditValue, getFieldsTouched } from '../utils/audit-labels';

export type AuditChangeEntry = { from?: unknown; to?: unknown };
export type AuditChanges = Record<string, AuditChangeEntry>;

export type AuditChangesDiffProps = {
  changes: AuditChanges | null;
  context: Record<string, unknown>;
  fidelity: 'domain' | 'facade';
  sourcePath: string | null;
};

export const formatTruncatedSize = (bytes: number): string => `${Math.max(1, Math.round(bytes / 1024))} KB`;

function DiffCell({ entry, side }: { entry: AuditChangeEntry; side: 'from' | 'to' }) {
  const { t } = useTranslation('settings');
  const formatted = formatAuditValue(entry[side]);

  if (formatted.kind === 'truncated') {
    return (
      <span className="text-muted-foreground italic">
        {t('auditTrail.diff.truncated', { size: formatTruncatedSize(formatted.bytes ?? 0) })}
      </span>
    );
  }

  if (formatted.kind === 'empty') {
    return (
      <span className="text-muted-foreground italic">
        {side in entry ? t('auditTrail.diff.emptyValue') : t('auditTrail.diff.notSet')}
      </span>
    );
  }

  if (formatted.kind === 'json') {
    return (
      <div className="overflow-x-auto">
        <pre className="text-xs">{formatted.text}</pre>
      </div>
    );
  }

  return <span>{formatted.text}</span>;
}

/**
 * Four mutually exclusive states, evaluated in order: a populated `changes`
 * diff always wins even on a `facade` row (a domain action can still record
 * `fidelity: 'facade'` metadata alongside a diff in edge cases), then the
 * facade notice, then a fields-touched list (credential rotations withhold
 * values on purpose), then "nothing recorded".
 */
export function AuditChangesDiff({ changes, context, fidelity, sourcePath }: AuditChangesDiffProps) {
  const { t } = useTranslation('settings');

  const changeEntries = changes ? Object.entries(changes) : [];
  const fieldsTouched = getFieldsTouched(context);

  return (
    <section className="space-y-2">
      <h4 className="text-sm font-semibold">{t('auditTrail.diff.header')}</h4>

      {changeEntries.length > 0 ? (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="px-3 py-2 text-left font-medium" />
                <th className="px-3 py-2 text-left font-medium">{t('auditTrail.diff.before')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('auditTrail.diff.after')}</th>
              </tr>
            </thead>
            <tbody>
              {changeEntries.map(([field, entry]) => (
                <tr key={field} className="border-b last:border-b-0">
                  <td className="px-3 py-2 font-mono text-xs">{field}</td>
                  <td className="px-3 py-2 align-top">
                    <DiffCell entry={entry} side="from" />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <DiffCell entry={entry} side="to" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : fidelity === 'facade' ? (
        <div className="text-muted-foreground text-sm">
          <p>{t('auditTrail.diff.facadeNotice')}</p>
          {sourcePath && <p className="mt-1 font-mono text-xs">{sourcePath}</p>}
        </div>
      ) : fieldsTouched ? (
        <div className="text-sm">
          <p className="text-muted-foreground">{t('auditTrail.diff.fieldsTouched')}</p>
          <ul className="mt-1 list-inside list-disc">
            {fieldsTouched.map((field) => (
              <li key={field} className="font-mono text-xs">
                {field}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">{t('auditTrail.diff.noDetail')}</p>
      )}
    </section>
  );
}
